import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Hardware } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hardware: Hardware | null;
  onSave: () => void;
  existingHardwareNames?: string[];
};

const initialFormData: Partial<Hardware> = {
  name: "",
  manufacture: "",
  product: "",
  part_number: "",
  capacity: "",
  colour: "",
  moq: null,
  bulk_cost: null,
  product_type: "battery",
  description: "",
  specification: "",
  images: [],
  currency_conversion_rate: 1.0,
  exchange_rate_buffer_percent: 0,
  shipping_cost: 0,
  gst_rate: 10,
  markup_rate: 0,
  markup_per_unit: 0,
  use_fixed_margin: false,
  gst_on_shipping: true,
  rounding: 0,
  visible_in_marketplace: false,
};

const productTypeOptions = ["battery", "dab pen", "white"];

const HardwareDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  hardware,
  onSave,
  existingHardwareNames = [],
}) => {
  const [formData, setFormData] = useState<Partial<Hardware>>(initialFormData);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (hardware) {
      setFormData({
        ...initialFormData,
        ...hardware,
      });
    } else {
      setFormData(initialFormData);
    }
  }, [hardware, open]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let processedValue: any = value;

    const numericFields = [
      "currency_conversion_rate", 
      "exchange_rate_buffer_percent", 
      "shipping_cost", 
      "gst_rate", 
      "markup_rate", 
      "markup_per_unit", 
      "rounding",
      "bulk_cost",
      "moq"
    ];

    if (numericFields.includes(name)) {
      processedValue = value === "" ? 0 : parseFloat(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  const handleSwitchChange = (field: string) => (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Only validate manufacture and product
      if (!formData.manufacture?.trim()) {
        toast({
          title: "Required Field Missing",
          description: "Manufacture name is required",
          variant: "destructive",
        });
        return;
      }

      if (!formData.product?.trim()) {
        toast({
          title: "Required Field Missing",
          description: "Product name is required",
          variant: "destructive",
        });
        return;
      }

      // Check for existing hardware with same combination
      const { data: existingHardware, error: checkError } = await supabase
        .from("hardware")
        .select("id")
        .eq("manufacture", formData.manufacture)
        .eq("product", formData.product)
        .eq("part_number", formData.part_number || '')
        .maybeSingle();

      if (checkError) {
        throw checkError;
      }

      if (existingHardware && (!hardware || existingHardware.id !== hardware.id)) {
        toast({
          title: "Duplicate Entry",
          description: "A hardware item with this combination already exists",
          variant: "destructive",
        });
        return;
      }

      const saveData = {
        name: formData.name || null,
        manufacture: formData.manufacture,
        product: formData.product,
        part_number: formData.part_number || null,
        capacity: formData.capacity || null,
        colour: formData.colour || null,
        moq: formData.moq || null,
        bulk_cost: formData.bulk_cost || null,
        product_type: formData.product_type || null,
        description: formData.description || null,
        specification: formData.specification || null,
        images: formData.images || [],
        currency_conversion_rate: formData.currency_conversion_rate || 1.0,
        exchange_rate_buffer_percent: formData.exchange_rate_buffer_percent || 0,
        shipping_cost: formData.shipping_cost || 0,
        gst_rate: formData.gst_rate || 10,
        markup_rate: formData.markup_rate || 0,
        markup_per_unit: formData.markup_per_unit || 0,
        use_fixed_margin: formData.use_fixed_margin || false,
        gst_on_shipping: formData.gst_on_shipping ?? true,
        rounding: formData.rounding || 0,
        visible_in_marketplace: formData.visible_in_marketplace || false,
      };

      let result;
      
      if (hardware) {
        result = await supabase
          .from("hardware")
          .update(saveData)
          .eq("id", hardware.id);
      } else {
        result = await supabase
          .from("hardware")
          .insert([saveData]);
      }

      if (result.error) throw result.error;
      
      toast({ 
        title: hardware ? "Hardware updated" : "Hardware created",
        description: `Successfully ${hardware ? 'updated' : 'created'} ${formData.product}` 
      });
      
      onSave();
      onOpenChange(false);
      
    } catch (error: any) {
      console.error("Error saving hardware:", error);
      
      let errorMessage = "An unexpected error occurred. Please try again.";
      if (error.code === '23505') {
        errorMessage = "This combination of manufacture and product already exists.";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl h-[90vh] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {hardware ? "Edit Hardware" : "Add Hardware"}
          </DialogTitle>
          <DialogDescription>
            Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
            </TabsList>

            <TabsContent value="details">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="manufacture">Manufacture *</Label>
                  <Input
                    id="manufacture"
                    name="manufacture"
                    value={formData.manufacture || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="product">Product *</Label>
                  <Input
                    id="product"
                    name="product"
                    value={formData.product || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="part_number">Part Number</Label>
                  <Input
                    id="part_number"
                    name="part_number"
                    value={formData.part_number || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="product_type">Product Type</Label>
                  <Select 
                    value={formData.product_type} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, product_type: value }))}
                  >
                    <SelectTrigger className="h-8 text-sm px-2 py-1">
                      <SelectValue placeholder="Select product type" />
                    </SelectTrigger>
                    <SelectContent>
                      {productTypeOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="capacity">Capacity</Label>
                  <Input
                    id="capacity"
                    name="capacity"
                    value={formData.capacity || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="colour">Colour</Label>
                  <Input
                    id="colour"
                    name="colour"
                    value={formData.colour || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="moq">MOQ</Label>
                  <Input
                    id="moq"
                    name="moq"
                    type="number"
                    value={formData.moq || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description || ""}
                    onChange={handleInputChange}
                    rows={3}
                    className="text-sm"
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <Label htmlFor="specification">Specification</Label>
                  <Textarea
                    id="specification"
                    name="specification"
                    value={formData.specification || ""}
                    onChange={handleInputChange}
                    rows={3}
                    className="text-sm"
                  />
                </div>

                
                <div className="space-y-2 col-span-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="visible_in_marketplace"
                      checked={formData.visible_in_marketplace || false}
                      onCheckedChange={handleSwitchChange("visible_in_marketplace")}
                    />
                    <Label htmlFor="visible_in_marketplace">Show in Marketplace</Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    When enabled, this item will be visible in the marketplace
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="pricing">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="currency_conversion_rate">Currency Conversion Rate</Label>
                  <Input
                    id="currency_conversion_rate"
                    name="currency_conversion_rate"
                    type="number"
                    step="0.01"
                    value={formData.currency_conversion_rate || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="exchange_rate_buffer_percent">Exchange Rate Buffer (%)</Label>
                  <Input
                    id="exchange_rate_buffer_percent"
                    name="exchange_rate_buffer_percent"
                    type="number"
                    step="0.01"
                    value={formData.exchange_rate_buffer_percent || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shipping_cost">Shipping Cost</Label>
                  <Input
                    id="shipping_cost"
                    name="shipping_cost"
                    type="number"
                    step="0.01"
                    value={formData.shipping_cost || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gst_rate">GST Rate (%)</Label>
                  <Input
                    id="gst_rate"
                    name="gst_rate"
                    type="number"
                    step="0.01"
                    value={formData.gst_rate || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="markup_rate">Markup Rate (%)</Label>
                  <Input
                    id="markup_rate"
                    name="markup_rate"
                    type="number"
                    step="0.01"
                    value={formData.markup_rate || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="markup_per_unit">Markup Per Unit</Label>
                  <Input
                    id="markup_per_unit"
                    name="markup_per_unit"
                    type="number"
                    step="0.01"
                    value={formData.markup_per_unit || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rounding">Rounding</Label>
                  <Input
                    id="rounding"
                    name="rounding"
                    type="number"
                    step="0.01"
                    value={formData.rounding || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bulk_cost">Bulk Cost</Label>
                  <Input
                    id="bulk_cost"
                    name="bulk_cost"
                    type="number"
                    step="0.01"
                    value={formData.bulk_cost || ""}
                    onChange={handleInputChange}
                    className="h-8 text-sm px-2 py-1"
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="use_fixed_margin"
                      checked={formData.use_fixed_margin || false}
                      onCheckedChange={handleSwitchChange("use_fixed_margin")}
                    />
                    <Label htmlFor="use_fixed_margin">Use Fixed Margin</Label>
                  </div>
                </div>

                <div className="space-y-2 col-span-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="gst_on_shipping"
                      checked={formData.gst_on_shipping ?? true}
                      onCheckedChange={handleSwitchChange("gst_on_shipping")}
                    />
                    <Label htmlFor="gst_on_shipping">GST on Shipping</Label>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : hardware ? "Save changes" : "Create Hardware"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HardwareDialog;
